import React, { useState, useEffect } from 'react';
import style from './style.module.css';

import { Link } from 'react-router-dom';

import { 
    getLanguageShortname, Language, 
    getLanguageArray, systemLanguage, getLabel,
    usersLabel, getOrganizations,
    getActiveOrganization, Organization,
    activeOrganization,
    getUuid, getToken, User,
    shortname,
    urlUser,
    urlActivity
} from '../../utils/constants';

import { ListUsers, UploadBulkUsers } from '../../services/userApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownUpAcrossLine, faBoxArchive, faChartBar, faEdit, faFile, faUpload} from '@fortawesome/free-solid-svg-icons';

import Icons from '../../parts/Icons/Icons';

import { CsvDownloadButton } from '../../components/Csv/Csv';
import Loader from '../../assets/images/loader.svg';

import { useNotification } from '../../utils/notifications';

const Users: React.FC = () => {

    const uuid = getUuid()
    const token = getToken()

    const notify = useNotification()

    const [sessionLang, setSessionLang] = useState<Language>(getLanguageArray());

    const [userLanguage, setUserLanguage] = useState<{ short: string | any } | false>(false);
    const user_language = getLanguageShortname();

    const [activeOrganizationName, setActiveOrganizationName] = useState<string | undefined>(undefined);
    const [activeOrganizationUserCount,setActiveOrganizationUserCount] = useState<number | undefined>(undefined);
    const [usersLoading,setUsersLoading] = useState<boolean>(true)
    const [userList, setUserList] = useState<User[]>([]);

    const [bulkUploading,setBulkUploading] = useState<boolean>(false)
    const [bulkUploadActive,setBulkUploadActive] = useState<boolean>(false)

    const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);

    const handleBulkUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setBulkUploadFile(e.target.files[0]); 
        }
    };

    const handleBulkUploadAction = async () => {
        if (!bulkUploadFile) return;

        setBulkUploading(true)

        const uploadBulkUsers = await UploadBulkUsers({
            file: bulkUploadFile,
            uuid: uuid,
            token: token,
            organization_id: activeOrganization
        })

        setBulkUploading(false)
        handleBulkImport()
        notify('Bulk file has successfully uploaded. Please wait while our system processes the file.', 3000)
        
    };

    const handleActiveOrganizationChange = async () => {
        setUsersLoading(true)

        const activeOrg = getActiveOrganization(); 
        const organizationsList: Organization[] = getOrganizations();
        const activeId = activeOrg?.id;
        const idToFind = typeof activeId === 'number' ? activeId : (organizationsList[0]?.id);
        if (typeof idToFind !== 'number') {
            setActiveOrganizationName(undefined);
            return;
        }
        const selectedOrg = organizationsList.find((org: Organization) => org.id === idToFind);
        const usersListData = await ListUsers({uuid: uuid, token: token, organization_id: selectedOrg?.id})

        setUserList(Array.isArray(usersListData) ? usersListData : []);
        setUsersLoading(false)

        setActiveOrganizationName(selectedOrg?.name ?? undefined);
        setActiveOrganizationUserCount(selectedOrg?.user_count ?? 0)
    }

    const handleBulkImport = () => {
        if(bulkUploadActive){
            setBulkUploadFile(null)
            setBulkUploadActive(false) 
            return
        }
        setBulkUploadActive(true)
    }

    const usersBlankData: User[] = [{
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        language: "",
        phone: "",
        country: "",
        address_street_city: "",
        postal_code: "",
        job_title: ""
    }];

    const renderBulkImport = () => (
      <div className={style.modal}>
        <div className={style.wrap}>
          <div className={`${style.langBox} ${style.orgBox}`}>
              <Icons icon={faBoxArchive} />
              <div className={style.langTitle}>Bulk Import</div>
              {
                !bulkUploading ?
                <>
                <div className={style.instructions}>
                    Please download the CSV template for bulk importing:
                    <span className={style.importButton}>
                        <CsvDownloadButton<User> data={usersBlankData} filename="phishnoo_bulk_upload_template.csv">
                            <FontAwesomeIcon icon={faFile} /> Download Template
                        </CsvDownloadButton>
                    </span>
                </div>
                <div className={style.uploader}>
                    Upload the complete CSV below to start the rendering process:
                    { 
                        !bulkUploadFile ? 
                        <span className={style.uploadSelectButton}>
                            <input type="file" onChange={handleBulkUploadFileChange} />
                        </span> : ''
                    }
                    {
                        bulkUploadFile ?
                            <>
                            <p>Selected file: {bulkUploadFile.name} <span className={style.removeFile} onClick={() => setBulkUploadFile(null)}>Remove</span></p>
                            <span className={style.uploadButton}>
                                <button onClick={handleBulkUploadAction} disabled={!bulkUploadFile}>
                                        <FontAwesomeIcon icon={faUpload} /> Upload CSV
                                </button>
                            </span>
                            </>
                        : ''
                    }
                </div>
                <div className={style.modalActions}>
                    <span onClick={handleBulkImport}>Cancel</span>
                </div>
                </> : 

                <div className={style.uploading}>
                    <img src={Loader} />
                </div>
             }
          </div>
        </div>
      </div>
    )

    const handleMoveUser = () => {}

    useEffect(() => {
        if (activeOrganizationName) return;
        handleActiveOrganizationChange()
    }, [activeOrganizationName]);

    useEffect(() => {
        if (user_language) {
            setUserLanguage({ short: user_language.toUpperCase() });
        } else {
            setUserLanguage({ short: ((user_language as unknown) as Language).short || 'EN' });
        }
    }, [user_language]);

    useEffect(() => {
        window.scrollTo(0,0);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === systemLanguage && e.newValue) {
                setSessionLang(JSON.parse(e.newValue));
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === activeOrganization && e.newValue) {
                handleActiveOrganizationChange()
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    return (
        <>
        <div className={style.scoreboard}>
            <div className={style.header}>
                <div className={style.headerLeft}>
                    <div className={`${style.greeting} ${style.textContainerGrad}`}>
                        {getLabel(usersLabel, sessionLang)}
                    </div>
                    <div className={style.info}>
                        {usersLoading ? "Loading..." : activeOrganizationName + ' (' + activeOrganizationUserCount + ')' }
                    </div>
                </div><div className={style.headerRight}>
                    <span onClick={handleBulkImport}>Bulk Import</span>
                    <Link to={'/user/create'}>Add User</Link>
                </div>
            </div>
            <div className={style.tableWrap}>
                <div className={`${style.tableRow} ${style.header}`}>
                    <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                        Name
                    </div><div className={`${style.tableCol} ${style.email}`}>
                        Email 
                    </div><div className={`${style.tableCol} ${style.level}`}>
                        Level
                    </div><div className={`${style.tableCol} ${style.exp}`}>
                        Experience
                    </div><div className={`${style.tableCol} ${style.shortname} ${style.actions}`}></div>
                </div>
                {!usersLoading && userList?.map((user: User) => (
                    <div className={style.tableRow} key={user.uuid}>
                        <div className={`${style.tableCol} ${style.shortname}`}>
                            <span>{shortname(user.firstname + ' ' + user.lastname)}</span>    
                        </div><div className={`${style.tableCol} ${style.name}`}>
                            <Link to={urlUser + '/' + user.uuid}>{user.firstname} {user.lastname}</Link>
                            <span>{user.job_title}</span>
                        </div><div className={`${style.tableCol} ${style.email}`}>
                            {user.email}
                        </div><div className={`${style.tableCol} ${style.level}`}>
                            {user.level}
                        </div><div className={`${style.tableCol} ${style.exp}`}>
                            {user.exp}
                        </div><div className={`${style.tableCol} ${style.shortname} ${style.actions}`}>
                            <Link to={urlUser + '/' + user.uuid + '/edit'} title={'Edit User'}>
                                <FontAwesomeIcon icon={faEdit} />
                            </Link><Link to={urlUser + '/' + user.uuid + urlActivity} title={'User Activity'}>
                                <FontAwesomeIcon icon={faChartBar} />
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        { bulkUploadActive && renderBulkImport()}
        </>
    );
}

export default Users;
